import React, { useState } from 'react';
import { ArrowLeft, Play, Loader2, AlertTriangle } from 'lucide-react';
import { mappingApi } from '../../services/api';
import type { OnboardingState, DictionaryResult, MappingTargetMode } from '../../types';

interface Props {
  source: OnboardingState;
  targetMode: MappingTargetMode;
  target: OnboardingState;
  targetDictionary: DictionaryResult | null;
  rateLimitRpm: number;
  setRateLimitRpm: (n: number) => void;
  description: string;
  setDescription: (s: string) => void;
  onBack: () => void;
  onStarted: (runId: string) => void;
}

const RunStep: React.FC<Props> = ({
  source, targetMode, target, targetDictionary,
  rateLimitRpm, setRateLimitRpm, description, setDescription,
  onBack, onStarted,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string>('');

  const sourceTableNames = source.selectedTables.map((t) => t.table_name);
  const targetTableNames = target.selectedTables.map((t) => t.table_name);

  const handleRun = async () => {
    setError('');
    setSubmitting(true);
    try {
      const payload: Record<string, any> = {
        source_connection: source.connection,
        source_schema: source.selectedSchema,
        source_tables: sourceTableNames,
        source_dictionary: source.dictionaryResult ?? null,
        target_mode: targetMode,
        rate_limit_rpm: rateLimitRpm,
        description: description || null,
      };

      if (targetMode === 'connection') {
        payload.target_connection = target.connection;
        payload.target_schema = target.selectedSchema;
        payload.target_tables = targetTableNames;
      } else {
        payload.target_dictionary = targetDictionary;
      }

      const res = await mappingApi.createRun(payload);
      if (res.data.status === 'success' && res.data.payload?.run_id) {
        onStarted(res.data.payload.run_id);
      } else {
        setError(res.data.message || 'Failed to start run');
      }
    } catch (e: any) {
      setError(e.response?.data?.message || e.message || 'Failed to start run');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="bg-cargo-panel border border-cargo-border rounded-lg p-6">
        <h3 className="text-cargo-text font-semibold mb-4">Review Configuration</h3>

        <div className="grid grid-cols-2 gap-6 text-sm">
          <div>
            <div className="text-cargo-muted text-xs uppercase tracking-wider mb-2">Source</div>
            <div className="space-y-1.5">
              <SummaryRow label="Connector" value={source.connection.connector_type ?? '—'} />
              <SummaryRow label="Host" value={source.connection.host ?? source.connection.account ?? '—'} />
              <SummaryRow label="Database" value={source.connection.database ?? '—'} />
              <SummaryRow label="Schema" value={source.selectedSchema ?? '—'} />
              <SummaryRow label="Tables" value={`${sourceTableNames.length} selected`} />
              <SummaryRow label="Dictionary" value={source.dictionaryResult ? 'Generated' : 'Not generated'} />
            </div>
          </div>

          <div>
            <div className="text-cargo-muted text-xs uppercase tracking-wider mb-2">Target</div>
            <div className="space-y-1.5">
              <SummaryRow label="Mode" value={targetMode} />
              {targetMode === 'connection' ? (
                <>
                  <SummaryRow label="Connector" value={target.connection.connector_type ?? '—'} />
                  <SummaryRow label="Host" value={target.connection.host ?? target.connection.account ?? '—'} />
                  <SummaryRow label="Database" value={target.connection.database ?? '—'} />
                  <SummaryRow label="Schema" value={target.selectedSchema ?? '—'} />
                  <SummaryRow label="Tables" value={`${targetTableNames.length} selected`} />
                </>
              ) : (
                <SummaryRow label="Tables" value={`${targetDictionary?.tables?.length ?? 0} in dictionary`} />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Config */}
      <div className="bg-cargo-panel border border-cargo-border rounded-lg p-6 space-y-4">
        <h3 className="text-cargo-text font-semibold">Run Configuration</h3>

        <div className="grid grid-cols-1 gap-4">
          <div>
            <label className="block text-cargo-muted text-xs font-medium mb-1.5">
              Description (optional)
            </label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g. Initial MDM mapping — helps you identify this run later"
              className="w-full px-3 py-2 bg-cargo-surface border border-cargo-border rounded-lg text-cargo-text text-sm focus:outline-none focus:ring-2 focus:ring-cargo-accent"
            />
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-cargo-error/10 border border-cargo-error/30 rounded-lg p-4 text-cargo-error text-sm flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Nav */}
      <div className="flex justify-between">
        <button
          onClick={onBack}
          disabled={submitting}
          className="inline-flex items-center gap-2 px-4 py-2 text-cargo-muted hover:text-cargo-text transition-colors disabled:opacity-50"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>
        <button
          onClick={handleRun}
          disabled={submitting}
          className="inline-flex items-center gap-2 px-5 py-2 bg-cargo-accent text-white rounded-lg hover:bg-cargo-accent/90 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Starting...
            </>
          ) : (
            <>
              <Play className="w-4 h-4" />
              Start Mapping
            </>
          )}
        </button>
      </div>
    </div>
  );
};

const SummaryRow: React.FC<{ label: string; value: string | number }> = ({ label, value }) => (
  <div className="flex items-center justify-between">
    <span className="text-cargo-muted">{label}</span>
    <span className="text-cargo-text font-medium font-mono text-xs">{String(value)}</span>
  </div>
);

export default RunStep;
